import * as yup from 'yup'
import { useState } from 'react'
import {
  Typography,
  useTheme,
  Stack,
  Dialog,
  IconButton,
  Button
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import colors from 'styles/colors'
import InCardStack from 'components/atoms/container/InCardStack'
import { useFormik } from 'formik'
import { validationsRules } from 'utils/formValidations'
import FormInputBox from 'components/atoms/input/FormInputBox'
import NotificationSnackBar from 'components/molecules/notificationOverlay/NotificationSnackBar'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import { OverlayWithText } from 'components/atoms/OverlayLoading'
import { ActionText } from 'components/atoms/Text'
import { setPublicUser } from 'utils/localStorage'
import { getRolePlayResponse } from 'services/rolePlayServices'
import { registerWithAmplitude, trackEvent } from 'services/amplitudeTracking'
import { MONARK_PRIVACY_POLICY_URL } from 'utils/constants'
import RolePlayScore from './RolePlayScore'

const FormInput = ({
  formik,
  attriburteName,
  label,
  placeholder,
  ...props
}) => {
  const theme = useTheme()
  return (
    <Stack gap={6} width={'100%'}>
      <Typography variant='body1' paddingX={12}>
        {label}
      </Typography>

      <FormInputBox
        sx={{ width: '100%' }}
        InputProps={{
          sx: {
            ...theme.typography.body1,
            backgroundColor: colors.White,
            borderRadius: 2.5
          }
        }}
        formik={formik}
        attributeName={attriburteName}
        placeholder={placeholder}
        variant='outlined'
        {...props}
      />
    </Stack>
  )
}

const PublicRolePlayGetResults = ({
  selectedUserRolePlay,
  resultPreview,
  sucessCallbackFn
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [successDialogOpen, setSuccessDialogOpen] = useState(false)
  const [openErrorSnack, setOpenErrorSnack] = useState(false)

  const hideDialog = () => {
    setOpenErrorSnack(false)
    setDialogOpen(false)
    formik.resetForm()
  }

  const submitResults = async values => {
    setLoading(true)
    const res = await getRolePlayResponse(
      selectedUserRolePlay?.rolePlayId,
      true,
      true,
      selectedUserRolePlay?.rolePlayMessages,
      selectedUserRolePlay?.conversationSeconds,
      null,
      selectedUserRolePlay?.type
    )
    if (res?.status === 200) {
      hideDialog()
      setSuccessDialogOpen(true)
      sucessCallbackFn({
        userRolePlay: res?.data?.data?.userRolePlay,
        userName: values.firstName
      })

      setTimeout(() => {
        setSuccessDialogOpen(false)
      }, 2500)
    } else {
      setOpenErrorSnack(true)
    }
    setLoading(false)
  }

  const formik = useFormik({
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
      jobTitle: ''
    },
    validateOnBlur: false,
    validationSchema: yup.object({
      email: validationsRules.email,
      firstName: validationsRules.textRequired,
      lastName: validationsRules.textRequired,
      jobTitle: validationsRules.textRequired
    }),
    onSubmit: async values => {
      setPublicUser(values)
      submitResults(values)
      // Log to amplitude when first submit form
      trackEvent('Submit Contact', { location: 'modal', email: values.email })
      registerWithAmplitude(values)
    }
  })

  const getResults = () => {
    setDialogOpen(true)
    trackEvent('Get Results', { location: 'modal' })
  }

  return (
    <>
      <OverlayWithText
        isOpen={loading}
        title={t('rolePlay.generatingResults')}
        message={t('rolePlay.holdTight')}
      />

      {/* Get Result Dialog */}
      <Dialog
        disableScrollLock
        open={resultPreview}
        sx={theme.publicRolePlay.result.getResultPopup}>
        {selectedUserRolePlay?.response?.userRolePlay?.parsedReview
          ?.ROLEPLAY_SCORE && (
          <RolePlayScore
            score={
              selectedUserRolePlay?.response?.userRolePlay?.parsedReview
                ?.ROLEPLAY_SCORE
            }
          />
        )}
        <InCardStack spacing={0} textAlign={'center'}>
          <Typography sx={theme.publicRolePlay.result.heading}>
            {t('publicRolePlay.getYourFullResults')}
          </Typography>
          <Typography variant='h4' fontWeight={400}>
            {t('publicRolePlay.toSeeFullResults')}
          </Typography>
        </InCardStack>

        <Button onClick={getResults}>
          {t('publicRolePlay.getFullResults')}
        </Button>
      </Dialog>

      {/* Lead Form Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={hideDialog}
        sx={theme.publicRolePlay.result.resultFormDialog}>
        <IconButton
          sx={theme.publicRolePlay.result.backMobileBtn}
          onClick={() => hideDialog()}>
          {<KeyboardBackspaceIcon />}
        </IconButton>
        <Stack sx={theme.publicRolePlay.result.resultFormStack}>
          <Stack spacing={0}>
            <Typography
              sx={theme.publicRolePlay.result.heading}
              style={{ textAlign: 'start' }}>
              {t('publicRolePlay.getYourFullResults')}
            </Typography>
            <Typography variant='h4' fontWeight={400}>
              {t('publicRolePlay.resultsAvailableOnFormSubmission')}
            </Typography>
          </Stack>

          <Stack sx={{ ...theme.publicRolePlay.stack, gap: 6 }}>
            <FormInput
              formik={formik}
              attriburteName='firstName'
              label={t('publicRolePlay.firstName')}
              placeholder={t('publicRolePlay.enterFirstName')}
            />

            <FormInput
              formik={formik}
              attriburteName='lastName'
              label={t('publicRolePlay.lastName')}
              placeholder={t('publicRolePlay.enterLastName')}
            />
          </Stack>

          <Stack sx={{ ...theme.publicRolePlay.stack, gap: 6 }}>
            <FormInput
              formik={formik}
              attriburteName='email'
              label={t('publicRolePlay.companyEmail')}
              placeholder={t('publicRolePlay.enterCompanyEmail')}
            />

            <FormInput
              formik={formik}
              attriburteName='jobTitle'
              label={t('publicRolePlay.jobTitle')}
              placeholder={t('publicRolePlay.enterJobTitle')}
            />
          </Stack>

          <Typography variant='body1'>
            {t('publicRolePlay.emailDiscalimer')}
          </Typography>
        </Stack>

        <Stack spacing={8}>
          <Button
            onClick={() => formik.submitForm()}
            disabled={loading || !formik.isValid}>
            {t('publicRolePlay.seeFullResults')}
          </Button>

          <Typography variant='body1'>
            {t('publicRolePlay.acceptTerms')}
            <ActionText
              href={MONARK_PRIVACY_POLICY_URL}
              target='_blank'
              sx={{ textDecoration: 'underline' }}>
              {t('publicRolePlay.termsAndConditions')}
            </ActionText>
          </Typography>
        </Stack>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        sx={theme.publicRolePlay.result.thanksDialog}>
        <InCardStack spacing={0} textAlign={'center'}>
          <Typography sx={theme.publicRolePlay.result.heading}>
            {t('publicRolePlay.thankYou')}
          </Typography>

          <InCardStack spacing={8}>
            <Typography variant='h4' fontWeight={400}>
              {t('publicRolePlay.redirectingToResults.part1')}
            </Typography>
            <Typography variant='h4' fontWeight={400}>
              {t('publicRolePlay.redirectingToResults.part2')}
            </Typography>
          </InCardStack>
        </InCardStack>
      </Dialog>

      <NotificationSnackBar
        open={openErrorSnack}
        setOpen={setOpenErrorSnack}
        error={true}
        errorText={t('settings.submitError')}
        autoHideDuration={3000}
      />
    </>
  )
}

export default PublicRolePlayGetResults
